import { cva } from 'class-variance-authority';
import {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type Ref,
  useId,
  useRef,
  useState,
} from 'react';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { IconButton } from './IconButton';

/**
 * The one text input (`FE-04.10`).
 *
 * Props are the union of all three Stage 2 returns, resolved by `B3-BRIEF.md`
 * §4: `label`/`hint`/`error`/`required`/`autoComplete`/`readOnly` are entry
 * surfaces', `leadingIcon`/`clearable`/`multiline` are finding-things',
 * `counter`/`autogrow` are card back's.
 *
 * Three constraints from the token layer that are easy to lose:
 *
 *  - **The resting edge is `--color-border-strong`, not `--color-border`.** All
 *    three returns found this independently: `--color-border` measures 1.30:1
 *    and is decorative only, which is below 1.4.11's 3:1 floor for a control
 *    boundary. Focus moves the edge to `--color-accent` — *in addition to* the
 *    global `:focus-visible` outline, never instead of it.
 *  - **Muted text only ever sits on an opaque surface.** `--color-panel` is
 *    92% and `--color-ink-muted` needs 98%, so the hint/error/counter row
 *    carries its own `bg-surface-raised` rather than trusting whatever the
 *    caller put behind the field.
 *  - **`multiline` is a boolean, not `as`.** `as` is reserved across this
 *    component set for element substitution (`Button`'s `as='a'`); two
 *    spellings for two different ideas.
 */

const fieldVariants = cva(
  [
    'w-full rounded-card border bg-surface-raised px-3 text-ink',
    'placeholder:text-ink-muted',
    // The edge that carries meaning. `border-border-strong` at rest, accent on
    // focus — an addition to the inherited focus ring, not a replacement.
    'border-border-strong focus:border-accent',
    'read-only:text-ink-muted',
    'disabled:cursor-not-allowed disabled:text-ink-muted',
  ],
  {
    variants: {
      invalid: { true: 'border-bad focus:border-bad', false: '' },
      multiline: { true: 'py-2 min-h-20 resize-y', false: 'h-9 py-1' },
      hasLeading: { true: 'pl-9', false: '' },
      hasTrailing: { true: 'pr-9', false: '' },
    },
    defaultVariants: {
      invalid: false,
      multiline: false,
      hasLeading: false,
      hasTrailing: false,
    },
  },
);

interface TextFieldOwnProps {
  /** Visible, always. Never placeholder-only — entry surfaces is explicit. */
  label: string;
  /** Muted helper text. Rendered on an opaque sheet; see the note above. */
  hint?: string;
  /** Sets `aria-invalid` **and** joins `aria-describedby`. */
  error?: string;
  /** Slot, not an icon name — the caller owns which icon set it uses. */
  leadingIcon?: ReactNode;
  /**
   * Renders a clear control once the field has a value. `onClear` is what
   * actually empties it; the field does not own the caller's state.
   */
  clearable?: boolean;
  onClear?: () => void;
  /** Defaults to `Clear <label>`. */
  clearLabel?: string;
  /**
   * Appears at `warnAt` and **not before** — a permanent 16,384-character
   * counter is noise on a two-word comment (`FR-COLLAB-001` edge case,
   * `FR-CARD-003` AC4).
   */
  counter?: { value: number; max: number; warnAt: number };
  /** Grows a multiline field to its content. No effect without `multiline`. */
  autogrow?: boolean;
  className?: string;
  /** Class names for the outer wrapper, when the caller needs to place it. */
  wrapperClassName?: string;
}

type SingleLineProps = TextFieldOwnProps &
  Omit<ComponentPropsWithoutRef<'input'>, 'children'> & {
    multiline?: false;
    ref?: Ref<HTMLInputElement>;
  };

type MultiLineProps = TextFieldOwnProps &
  Omit<ComponentPropsWithoutRef<'textarea'>, 'children'> & {
    multiline: true;
    ref?: Ref<HTMLTextAreaElement>;
  };

export type TextFieldProps = SingleLineProps | MultiLineProps;

/**
 * One widened shape to destructure from. The exported union is what callers
 * see; inside, `input` and `textarea` props are structurally close enough that
 * a single spread is honest and a second implementation is not.
 */
type WidenedProps = TextFieldOwnProps &
  Omit<ComponentPropsWithoutRef<'input'>, 'ref'> &
  Omit<ComponentPropsWithoutRef<'textarea'>, 'ref'> & {
    multiline?: boolean;
    ref?: Ref<HTMLInputElement> | Ref<HTMLTextAreaElement>;
  };

export function TextField(props: TextFieldProps) {
  const { t } = useInterfaceLanguage();
  const {
    label,
    hint,
    error,
    leadingIcon,
    clearable,
    onClear,
    clearLabel,
    counter,
    autogrow,
    className,
    wrapperClassName,
    multiline,
    id: idProp,
    required,
    'aria-describedby': describedByProp,
    'aria-invalid': ariaInvalidProp,
    onChange,
    ref,
    ...rest
  } = props as WidenedProps;

  const reactId = useId();
  const id = idProp ?? `${reactId}-field`;
  const hintId = `${reactId}-hint`;
  const errorId = `${reactId}-error`;
  const counterId = `${reactId}-counter`;

  const showCounter = counter !== undefined && counter.value >= counter.warnAt;

  // Composed as a list, not overwritten: hint, error and counter can all be
  // present at once, and the caller may pass its own id through the native
  // prop. Overwriting instead of joining is the standard bug here.
  const describedBy =
    [
      hint ? hintId : null,
      error ? errorId : null,
      showCounter ? counterId : null,
      describedByProp,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  // Tracks whether the field has content so `clearable` works for controlled
  // and uncontrolled callers alike.
  const initialHasValue = String(rest.value ?? rest.defaultValue ?? '').length > 0;
  const [hasValue, setHasValue] = useState(initialHasValue);
  const controlledHasValue =
    rest.value === undefined ? hasValue : String(rest.value).length > 0;

  const innerRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  function handleChange(event: ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) {
    setHasValue(event.currentTarget.value.length > 0);
    if (autogrow && multiline) {
      // jsdom reports no layout, so `scrollHeight` is 0 there and this is a
      // no-op under test. Verified in a real browser only.
      const el = event.currentTarget;
      el.style.height = 'auto';
      el.style.height = `${String(el.scrollHeight)}px`;
    }
    onChange?.(event);
  }

  const showClear = clearable === true && controlledHasValue && rest.readOnly !== true;
  const invalid =
    error !== undefined || ariaInvalidProp === true || ariaInvalidProp === 'true';

  const controlClassName = cn(
    fieldVariants({
      invalid,
      multiline: multiline === true,
      hasLeading: leadingIcon !== undefined,
      hasTrailing: showClear,
    }),
    className,
  );

  const shared = {
    ...rest,
    id,
    required,
    'aria-describedby': describedBy,
    'aria-invalid': invalid ? true : undefined,
    onChange: handleChange,
    className: controlClassName,
  };

  return (
    <div className={cn('flex flex-col gap-1', wrapperClassName)}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required === true && (
          // The asterisk is decoration; `required` on the control is the fact.
          <span aria-hidden="true" className="ml-0.5 text-bad">
            *
          </span>
        )}
      </label>

      <div className="relative">
        {leadingIcon !== undefined && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 flex w-9 items-center justify-center text-ink-muted"
          >
            {leadingIcon}
          </span>
        )}

        {multiline === true ? (
          <textarea
            {...shared}
            ref={(node) => {
              innerRef.current = node;
              assignRef(ref as Ref<HTMLTextAreaElement> | undefined, node);
            }}
          />
        ) : (
          <input
            {...shared}
            ref={(node) => {
              innerRef.current = node;
              assignRef(ref as Ref<HTMLInputElement> | undefined, node);
            }}
          />
        )}

        {showClear && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-1">
            <IconButton
              type="button"
              size="sm"
              label={clearLabel ?? t('primitives.textField.clear', { label })}
              onClick={() => {
                setHasValue(false);
                onClear?.();
                innerRef.current?.focus();
              }}
            >
              <ClearGlyph />
            </IconButton>
          </span>
        )}
      </div>

      {(hint !== undefined || error !== undefined || showCounter) && (
        // Opaque, because everything in this row is muted or status-coloured
        // and `--color-panel` is below the floor for both.
        <div className="flex items-start justify-between gap-3 rounded-card bg-surface-raised px-1 py-0.5 text-xs">
          <span className="flex flex-col gap-0.5">
            {hint !== undefined && (
              <span id={hintId} className="text-ink-muted">
                {hint}
              </span>
            )}
            {error !== undefined && (
              <span id={errorId} className="text-bad">
                {error}
              </span>
            )}
          </span>
          {showCounter && counter && (
            <span
              id={counterId}
              className={cn(
                'shrink-0 tabular-nums',
                counter.value > counter.max ? 'text-bad' : 'text-warn',
              )}
            >
              {counter.value}/{counter.max}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function assignRef<T>(ref: Ref<T> | undefined, node: T | null) {
  if (typeof ref === 'function') ref(node);
  else if (ref) (ref as { current: T | null }).current = node;
}

function ClearGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="size-3.5" aria-hidden="true" focusable="false">
      <path
        d="M4 4l8 8M12 4l-8 8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}
